import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthProvider.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import { api } from "../lib/api.js";
import { Button } from "../components/ui/button.js";
import { Download } from "lucide-react";

function EpisodesPageWithErrorBoundary() {
  return (
    <ErrorBoundary componentName="EpisodesPage">
      <EpisodesPage />
    </ErrorBoundary>
  );
}

export const Route = createFileRoute("/episodes/")({ component: EpisodesPageWithErrorBoundary });

interface Episode {
  id: string;
  title: string;
  number?: string;
  status: string;
  scheduledAt?: string;
  guest: { id: string; name: string };
}

const statusOrder = ["inquiry", "scheduled", "recorded", "edited", "published", "promoted", "completed"];
const statusLabels: Record<string, string> = { inquiry: "Inquiry", scheduled: "Scheduled", recorded: "Recorded", edited: "Edited", published: "Published", promoted: "Promoted", completed: "Completed" };
const statusColors: Record<string, string> = { inquiry: "bg-chart-3/10 text-chart-3", scheduled: "bg-primary/10 text-primary", recorded: "bg-chart-1/10 text-chart-1", edited: "bg-chart-5/10 text-chart-5", published: "bg-chart-2/10 text-chart-2", promoted: "bg-chart-4/10 text-chart-4", completed: "bg-muted text-muted-foreground" };

function EpisodesPage() {
  const { user } = useAuth();
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get("/api/episodes/export");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `guestflow-episodes-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    const url = filter === "all" ? "/api/episodes" : `/api/episodes?status=${filter}`;
    api.get(url).then(r => r.json()).then(d => { setEpisodes(d.episodes); setLoading(false); });
  }, [filter, user]);

  if (!user) return <div>Loading...</div>;

  const grouped = episodes.reduce((acc, ep) => { acc[ep.status] = acc[ep.status] || []; acc[ep.status].push(ep); return acc; }, {} as Record<string, Episode[]>);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Episodes</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} disabled={exporting || episodes.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
          <Link to="/episodes/new" search={{}} className="px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium">+ Schedule Episode</Link>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-md text-sm font-medium ${filter === "all" ? "bg-primary text-primary-foreground" : "border hover:bg-muted"}`}>All</button>
        {statusOrder.map(s => <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-md text-sm font-medium ${filter === s ? "bg-primary text-primary-foreground" : "border hover:bg-muted"}`}>{statusLabels[s]}</button>)}
      </div>
      {loading ? <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div> : episodes.length === 0 ? (
        <div className="text-center py-12 border rounded-lg border-dashed">
          <p className="text-muted-foreground mb-4">No episodes yet.</p>
          <Link to="/episodes/new" search={{}} className="px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium">Schedule your first episode</Link>
        </div>
      ) : filter === "all" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          {statusOrder.map(status => (
            <div key={status} className="space-y-3">
              <div className="flex items-center justify-between"><h3 className="font-medium text-sm">{statusLabels[status]}</h3><span className="text-xs text-muted-foreground">{grouped[status]?.length || 0}</span></div>
              <div className="space-y-2">
                {grouped[status]?.map(ep => (
                  <Link key={ep.id} to="/episodes/$episodeId" params={{ episodeId: ep.id }} className="block p-3 border rounded-md hover:border-primary transition-colors bg-card">
                    <div className="font-medium text-sm line-clamp-2">{ep.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{ep.guest.name}</div>
                    {ep.scheduledAt && <div className="text-xs text-muted-foreground">{new Date(ep.scheduledAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>}
                  </Link>
                ))}
                {!grouped[status]?.length && <div className="p-4 border border-dashed rounded-md text-center"><span className="text-xs text-muted-foreground">No episodes</span></div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted"><tr><th className="px-4 py-3 text-left text-sm font-medium">Episode</th><th className="px-4 py-3 text-left text-sm font-medium">Guest</th><th className="px-4 py-3 text-left text-sm font-medium">Date</th><th className="px-4 py-3 text-left text-sm font-medium">Status</th></tr></thead>
            <tbody>
              {episodes.map(ep => (
                <tr key={ep.id} className="border-t hover:bg-muted/50">
                  <td className="px-4 py-3"><Link to="/episodes/$episodeId" params={{ episodeId: ep.id }} className="font-medium hover:text-primary">{ep.title}</Link>{ep.number && <span className="text-sm text-muted-foreground ml-2">#{ep.number}</span>}</td>
                  <td className="px-4 py-3 text-sm"><Link to="/guests/$guestId" params={{ guestId: ep.guest.id }} className="hover:text-primary">{ep.guest.name}</Link></td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{ep.scheduledAt ? new Date(ep.scheduledAt).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded-full ${statusColors[ep.status]}`}>{statusLabels[ep.status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
